import type { ReactNode } from 'react';
import { Navigate } from 'react-router';
import { useGroupSecurityCohort } from '@/hooks/useGroupSecurityCohort';

/** Where an OFF-cohort account's user-management lives. */
export const ACCOUNT_MEMBERS_PATH = '/app/s/account-members';
/** Where an ON-cohort account's user-management lives. */
export const DIRECTORY_HOME_PATH = '/app/s/teamspaces';

/**
 * Route gate for the Directory pages (Users / Teamspaces / Roles / Groups) — ON cohort only
 * (#2452, spec §2 UM-COHORT-2).
 *
 * Hiding the nav entries (`useUserManagementAccess`) is not enough on its own: these URLs have been
 * live for months, so they are bookmarked, pasted into tickets, and linked from emails. An OFF
 * account following one must land somewhere that answers the same question, which is Account
 * Members — NOT Screens, and NOT a 404. That is the whole reason this is a redirect to a sibling
 * page rather than the `UserManagementRouteGuard`'s bounce to `/app/screenManagement`.
 *
 * `replace` so the redirect does not trap the Back button between the two.
 */
export function GroupSecurityCohortRoute({ children }: { children: ReactNode }) {
  const cohort = useGroupSecurityCohort();
  if (!cohort) return <Navigate to={ACCOUNT_MEMBERS_PATH} replace />;
  return <>{children}</>;
}

/**
 * The inverse, for `/app/s/account-members` itself.
 *
 * ON cohort  — the page is superseded by the Directory; keep today's redirect to Teamspaces.
 * OFF cohort — render the real page. It was never deleted, only unlinked (`show: false` in
 *              SettingsLayout + this redirect), which is exactly why "keep the Account Members
 *              page for existing customers" is a routing change and not a rebuild.
 *
 * The scio1 `hideAccountMembersPage` branding gate still applies on top, in SettingsLayout.
 */
export function AccountMembersCohortRoute({ children }: { children: ReactNode }) {
  const cohort = useGroupSecurityCohort();
  if (cohort) return <Navigate to={DIRECTORY_HOME_PATH} replace />;
  return <>{children}</>;
}
